import { useEffect, useState } from 'react';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import * as machine from '../lib/till.js';

/**
 * The machine you are standing at.
 *
 * Separate from Businesses and tills, which is a super admin looking at every counter in six
 * businesses from anywhere. This is one person at one counter with a printer that has stopped,
 * and it needs a different answer to "who is allowed".
 *
 * Registering a till, revoking a device key and deciding which counters exist stay where they
 * were. Telling this machine which printer is plugged into it does not: a manager on a
 * Saturday cannot wait for somebody with the top rung to be free, and the settings written
 * here never leave the machine anyway.
 *
 * Everything on this screen talks to the desktop shell, never to the server, which is the
 * point. It has to work when the connection is the thing that is broken.
 */

const PAIR_ERRORS = {
  bad_code: 'That code is not right. Ask for it again.',
  code_expired: 'That code has run out. Ask for a new one.',
  code_required: 'Type the code first.',
  till_disabled: 'That till has been switched off. A super admin has to turn it back on.',
  unreachable: 'This machine cannot reach EPMS, so it cannot pair yet.'
};

// Hosted on the same server the till already talks to, so a machine can fetch its own
// update instead of waiting for somebody to walk over with a USB stick.
const INSTALLER = '/download/EPMS-Setup-latest.exe';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const Head = ({ children }) => (
  <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
    {children}
  </div>
);

const TRANSPORTS = [
  { name: 'spooler', label: 'USB, straight to the printer',
    hint: 'The printer is plugged into this machine. Pick it by the name Windows calls it. Nothing has to be shared.',
    field: 'Printer name', placeholder: 'XP-80C' },
  { name: 'windows', label: 'USB, through a Windows share',
    hint: 'The printer is shared in Windows and reached by its share name. Works, but sharing has to be switched on first.',
    field: 'Share name', placeholder: 'XP-80C' },
  { name: 'network', label: 'Over the network',
    hint: 'The printer has its own address on the salon network.',
    field: 'Address', placeholder: '192.168.1.50' },
  { name: 'file', label: 'To a file, for diagnosis',
    hint: 'Writes the bytes to a file instead of printing, to see what would have come out.',
    field: 'File path', placeholder: 'C:\\epms\\receipt.bin' }
];

/**
 * The printers Windows actually has, so nobody types a share name from memory.
 *
 * Windows shows a printer under one name and shares it under another, and very often does not
 * share it at all. The only symptom of getting it wrong is a write failing with "unknown
 * error" against a path that looks perfectly reasonable, which is where this screen was
 * before it could show the list.
 */
function Printers({ found, onPick, byName }) {
  if (found.shellTooOld) {
    return (
      <Banner tone="info" title="This copy of the till app cannot list them">
        Type the name instead, or reinstall EPMS to get the list. The app updates its own
        screens from the server, but reading the printers off Windows is part of the desktop
        app itself and only changes when it is reinstalled.
      </Banner>
    );
  }
  if (found.platform !== 'win32') return null;

  if (!found.printers.length) {
    return (
      <Banner tone="warning" title="Windows reports no printers on this machine">
        The printer has to be installed in Windows first, under Settings, Printers and
        scanners. Until it is there, nothing can be shared and nothing can be printed to.
      </Banner>
    );
  }

  // Straight to the printer needs no sharing, so every printer is offered by its own name.
  // Through a share can only reach the ones that are actually shared.
  const usable = byName ? found.printers : found.printers.filter(p => p.shared && p.shareName);
  const rest = byName ? [] : found.printers.filter(p => !p.shared || !p.shareName);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {usable.length > 0 && (
        <>
          <div className="t-label-xs muted">ON THIS MACHINE, TAP ONE</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {usable.map(p => (
              <button key={p.name} onClick={() => onPick({ target: byName ? p.name : p.shareName })}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}
                title={`${p.name} on ${p.port || 'unknown port'}`}>
                <Chip tone="accent" size="m" showDot={false}>{byName ? p.name : p.shareName}</Chip>
              </button>
            ))}
          </div>
        </>
      )}

      {rest.length > 0 && (
        <Banner tone={usable.length ? 'info' : 'warning'}
          title={usable.length ? 'Installed but not shared' : 'Nothing is shared yet'}>
          {rest.map(p => p.name).join(', ')}.
          {' '}Either switch to "straight to the printer" above, which needs no sharing at all,
          or share it: Printers and scanners, pick the printer, Printer properties, Sharing,
          tick Share this printer.
        </Banner>
      )}
    </div>
  );
}

export function ThisTill() {
  const [here, setHere] = useState(null);
  const [printer, setPrinter] = useState(null);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [note, setNote] = useState(null);
  const [code, setCode] = useState('');
  const [pairError, setPairError] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [version, setVersion] = useState(null);
  const [found, setFound] = useState(null);
  const [queue, setQueue] = useState(null);

  const load = () => machine.localSettings().then(cfg => {
    setHere(cfg);
    setPrinter(p => p || { ...(cfg?.printer || {}) });
  }).catch(() => {});

  useEffect(() => {
    load();
    machine.version().then(setVersion).catch(() => {});
    machine.printers().then(setFound).catch(() => {});
    checkQueue();
  }, []);

  const checkQueue = () => machine.printerQueue().then(setQueue).catch(() => {});

  if (!machine.onTill) {
    return (
      <Banner tone="info" title="This screen belongs to a till">
        You are looking at EPMS in a browser. A printer and a cash drawer are wired to a
        machine, so this only means anything inside the till app on that machine.
      </Banner>
    );
  }

  if (!here || !printer) return <div className="t-body-m muted">Reading this machine</div>;

  const set = patch => { setPrinter(p => ({ ...p, ...patch })); setResult(null); };

  /*
   * Only offer connections this copy of the app can actually make.
   *
   * The screens update themselves and the shell around them does not, so a page can offer a
   * transport the app has never heard of. Choosing one then fails at the moment of a sale
   * with "unknown transport", which is a sentence about software rather than about printing
   * and leaves somebody with a customer waiting.
   *
   * Straight to the printer arrived in 0.2.0. Anything older gets the share, which is what
   * it has always been able to do.
   */
  const shellVersion = version ? String(version.app || '0.0.0') : null;
  const knowsSpooler = !shellVersion || Number(shellVersion.split('.')[1] || 0) >= 2;
  const offered = TRANSPORTS.filter(t => t.name !== 'spooler' || knowsSpooler);

  const transport = offered.find(t => t.name === printer.transport) || offered[0];
  const savedTarget = here.printer?.target || null;
  const unsaved = Boolean(printer.target) && printer.target !== savedTarget;

  async function run(what, fn) {
    setBusy(what); setResult(null);
    try {
      const r = await fn();
      setResult(r.sent === false ? { ok: false, message: machine.printerProblem(r) || r.error } : { ok: true, ...r });
    } catch (e) {
      setResult({ ok: false, message: e.message });
    } finally { setBusy(null); }
  }

  const testThenCheck = what => run(what, async () => {
    const r = what === 'test' ? await machine.testPrint(printer) : await machine.openDrawer(printer);
    // Accepted is not printed, so look at the queue immediately rather than reporting a
    // success that has not happened yet.
    if (r.sent) setTimeout(checkQueue, 1200);
    return r;
  });

  const save = () => run('save', async () => {
    await machine.saveLocalSettings({ printer });
    await load();
    setNote('Saved on this machine. Completed sales will print by themselves from now on.');
    return { sent: true, saved: true };
  });

  async function pair(e) {
    e.preventDefault();
    setBusy('pair'); setPairError(null);
    const r = await machine.pair(code.trim());
    setBusy(null);
    if (!r.paired) return setPairError(PAIR_ERRORS[r.error] || `Pairing failed: ${r.error}`);
    setCode('');
    setRepairing(false);
    setNote(`This machine is now till ${r.till.code}, ${r.till.name}.`);
    load();
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      {note && <Banner tone="success">{note}</Banner>}

      {/* ---- which till this is */}
      <div style={card}>
        <Head>WHICH TILL THIS IS</Head>
        <div style={{ padding: '14px 16px', display: 'grid', gap: 12 }}>
          {here.paired && !repairing ? (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip tone="success">Paired as {here.tillCode}</Chip>
                <span className="t-body-m">{here.tillName}</span>
                <span style={{ flex: 1 }} />
                <Button size="m" variant="secondary" onClick={() => setRepairing(true)}>
                  Pair to a different till
                </Button>
              </div>
              {/* There is no unpair, and there does not need to be. Pairing writes over the
                  key, the code and the name, so putting a machine on the right till is one
                  action rather than two. Hiding the box while it is paired was the whole
                  problem: a code typed on the wrong machine left nowhere to correct it. */}
              <div className="t-body-s muted">
                If this is the wrong counter, pair it again. The new code replaces this one,
                and the old key stops working the moment a super admin revokes it.
              </div>
            </div>
          ) : (
            <>
              {here.paired && (
                <Banner tone="info" title={`This machine is currently ${here.tillCode}`}>
                  Typing a new code moves it to that till instead. Nothing is lost: anything
                  waiting to be sent goes up under the till it was rung up on.
                </Banner>
              )}
              <Banner tone="warning" title="Not paired yet">
                Sales from this machine are going into the books under W1, the fallback, rather
                than under this counter's own code. Pairing also lets it send up sales it took
                while the connection was down. Ask a super admin to start pairing, then type
                the code they read out.
              </Banner>
              <form onSubmit={pair} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ width: 220 }}>
                  <Field id="pair-code" label="PAIRING CODE" value={code} autoFocus
                    onChange={e => setCode(e.target.value.toUpperCase())} />
                </div>
                <Button type="submit" size="m" disabled={busy === 'pair' || !code.trim()}>
                  {busy === 'pair' ? 'Pairing' : 'Pair this machine'}
                </Button>
                {here.paired && (
                  <Button type="button" size="m" variant="ghost"
                    onClick={() => { setRepairing(false); setCode(''); setPairError(null); }}>
                    Cancel
                  </Button>
                )}
              </form>
              {pairError && <Banner tone="danger">{pairError}</Banner>}
            </>
          )}
        </div>
      </div>

      {/* ---- the printer */}
      <div style={card}>
        <Head>THE PRINTER ON THIS MACHINE</Head>
        <div style={{ padding: '12px 16px', display: 'grid', gap: 12 }}>
          {machine.queueProblem(queue) && (
            <Banner tone="danger" title="Windows is taking the work and not printing it">
              {machine.queueProblem(queue)}
              {queue.queued > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button size="m" variant="secondary" disabled={busy === 'queue'}
                    onClick={() => run('queue', async () => {
                      const r = await machine.clearQueue();
                      await checkQueue();
                      setNote(`${r.cleared} waiting job${r.cleared === 1 ? '' : 's'} thrown away.`);
                      return { sent: true, saved: true };
                    })}>
                    {busy === 'queue' ? 'Clearing' : `Throw away the ${queue.queued} waiting`}
                  </Button>
                  <span className="t-body-s muted">
                    They are reprints of sales already on the books. Printing them once the
                    printer wakes up is a roll of paper and a bin.
                  </span>
                </div>
              )}
            </Banner>
          )}

          {savedTarget && printer.transport === 'spooler' && !knowsSpooler && (
            <Banner tone="danger" title="This machine is set to a connection it cannot use">
              The printer is set to go straight to the printer, which arrived in EPMS 0.2.0, and
              this copy is {shellVersion}. Nothing will print until you either install the
              current version or switch to the Windows share below and save.
            </Banner>
          )}

          {!savedTarget && (
            <Banner tone="warning" title="No printer saved on this machine">
              Completed sales will not print by themselves until there is one. Set it below and
              press Save.
            </Banner>
          )}

          <div style={{ display: 'grid', gap: 8 }}>
            {offered.map(t => (
              <label key={t.name} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, cursor: 'pointer',
                border: '1px solid ' + (printer.transport === t.name ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                background: printer.transport === t.name ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
                borderRadius: 'var(--radius-md)',
                opacity: t.name === 'windows' && version && version.platform !== 'win32' ? 0.55 : 1
              }}>
                <input type="radio" name="transport" checked={printer.transport === t.name}
                  onChange={() => set({ transport: t.name })} style={{ marginTop: 3 }} />
                <span>
                  <span className="t-body-m">{t.label}</span>
                  <span className="t-body-s muted" style={{ display: 'block' }}>{t.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <Field id="target" label={(transport.field || 'TARGET').toUpperCase()}
            value={printer.target || ''} placeholder={transport.placeholder}
            onChange={e => set({ target: e.target.value })} />

          {(printer.transport === 'windows' || printer.transport === 'spooler') && found &&
            <Printers found={found} onPick={set} byName={printer.transport === 'spooler'} />}
        </div>

        {/* ---- the drawer, the reason this screen has to be quick */}
        <Head>THE CASH DRAWER</Head>
        <div style={{ padding: '12px 16px', display: 'grid', gap: 12 }}>
          <div className="t-body-s muted">
            The drawer hangs off the printer, not off this computer, and opens on a pulse.
            Which pin and how long differ between units, so there is nothing to look up:
            change a value, press Open the drawer, and watch.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 140 }}>
              <div className="t-label-xs muted" style={{ marginBottom: 4 }}>PIN</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[0, 1].map(pin => (
                  <button key={pin} onClick={() => set({ drawerPin: pin })}
                    style={{
                      flex: 1, padding: '10px 0', cursor: 'pointer', font: 'inherit',
                      border: '1px solid ' + (printer.drawerPin === pin ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                      background: printer.drawerPin === pin ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
                      borderRadius: 'var(--radius-md)'
                    }}>{pin}</button>
                ))}
              </div>
            </div>
            <div style={{ width: 150 }}>
              <Field id="on" label="PULSE ON, MS" value={printer.drawerOnMs ?? 50} inputMode="numeric"
                onChange={e => set({ drawerOnMs: Number(e.target.value) || 0 })} />
            </div>
            <div style={{ width: 150 }}>
              <Field id="off" label="PULSE OFF, MS" value={printer.drawerOffMs ?? 200} inputMode="numeric"
                onChange={e => set({ drawerOffMs: Number(e.target.value) || 0 })} />
            </div>
          </div>
        </div>

        {/* ---- do it */}
        <div style={{ padding: 16, display: 'grid', gap: 12, borderTop: '1px solid var(--color-border-default)' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="m" disabled={busy === 'test' || !printer.target}
              onClick={() => testThenCheck('test')}>
              {busy === 'test' ? 'Printing' : 'Print a test page'}
            </Button>
            <Button size="m" variant="secondary" disabled={busy === 'drawer' || !printer.target}
              onClick={() => testThenCheck('drawer')}>
              {busy === 'drawer' ? 'Opening' : 'Open the drawer'}
            </Button>
            <span style={{ flex: 1 }} />
            <Button size="m" variant={unsaved ? 'primary' : 'secondary'}
              disabled={busy === 'save' || !printer.target} onClick={save}>
              {busy === 'save' ? 'Saving' : 'Save on this machine'}
            </Button>
          </div>

          {unsaved && (
            <Banner tone="warning" title="Not saved yet">
              Test and Open the drawer use what is on this screen. Until you press Save, this
              machine still prints with {savedTarget ? `"${savedTarget}"` : 'nothing at all'},
              and a completed sale will not print by itself.
            </Banner>
          )}

          {result && (result.ok
            ? <Banner tone="success" title={result.saved ? 'Saved' : 'Sent'}>
                {result.saved
                  ? 'This is now what the machine prints with.'
                  : `${result.bytes} bytes over ${result.transport} to ${result.target}, in ${result.ms}ms. `
                    + 'Windows took it. Look at the paper: the ruler line should end on an 8. '
                    + 'If nothing came out, the printer is not taking the work and the warning above will say so.'}
              </Banner>
            : <Banner tone="danger" title="It did not work">{result.message}</Banner>)}
        </div>
      </div>

      {version && !machine.can('savePdf') && (
        <Banner tone="warning" title="This copy of the till app is out of date">
          The screens keep themselves up to date from the server, but the app around them only
          changes when it is reinstalled. This copy is EPMS {version.app}, and it cannot save a
          report as a file, so Download as PDF falls back to the print dialog.
          {' '}<a href={INSTALLER} download>Download the current installer</a> and run it on
          this machine. Nothing is lost: the printer settings, the pairing and anything waiting
          to be sent all stay where they are.
        </Banner>
      )}

      <div style={card}>
        <Head>THE SCREEN</Head>
        <div style={{ padding: '14px 16px', display: 'grid', gap: 10 }}>
          <div className="t-body-s muted">
            Normally maximised, which fills the screen and leaves the taskbar where it is, so
            Windows' own on-screen keyboard is one tap away on a touch panel. Full screen fills
            it too but hides the taskbar, and that keyboard with it, so EPMS puts up a plain one
            of its own only in that case. Maximised is the better setting on a touch screen.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button size="m" variant="secondary" disabled={busy === 'screen'}
              onClick={() => run('screen', async () => {
                const next = !here.fullScreen;
                await machine.saveLocalSettings({ fullScreen: next });
                await load();
                setNote(next
                  ? 'Full screen. The taskbar is hidden until you turn this off again.'
                  : 'Maximised. It still fills the screen, and the taskbar is back.');
                return { sent: true, saved: true };
              })}>
              {here.fullScreen ? 'Leave full screen' : 'Go full screen'}
            </Button>
            <span className="t-body-s muted">
              Older copies of the app only apply this the next time it starts.
            </span>
          </div>
        </div>
      </div>

      <div style={card}>
        <Head>THE TILL APP</Head>
        <div style={{ padding: '14px 16px', display: 'grid', gap: 10 }}>
          <div className="t-body-s muted">
            The screens update themselves whenever this machine is online. The app around them,
            which is what reaches the printer and the drawer, only changes when it is
            reinstalled. Run the installer on any machine that needs to be a till.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button size="m" variant="secondary" onClick={() => { window.location.href = INSTALLER; }}>
              Download the installer
            </Button>
            <span className="t-body-s muted">
              Windows will warn that it is unrecognised. More info, then Run anyway.
            </span>
          </div>
        </div>
      </div>

      {version && (
        <div className="t-body-s muted">
          EPMS {version.app} on this machine.
          {version.screen && ` Screen ${version.screen.resolution} at ${Math.round(version.screen.scale * 100)}%, `
            + `so the layout gets ${version.screen.effective}.`}
        </div>
      )}
    </div>
  );
}
